import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Sparkles, X } from 'lucide-react';
import { buildAutofillProposal, type ProposedAssignment } from '@shared/autofill';
import type { EngineAbsence, EngineAssignment, EnginePerson } from '@shared/conflicts';
import type { Assignment, Availability, Personnel, Qualification } from '@shared/types';
import type { SchedulingRule } from '@shared/conflicts';
import { formatHours, formatRange } from '@shared/format';
import { DAY } from '@shared/time';
import { t } from '@/i18n';
import { ApiError, api } from '@/lib/api';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { IconButton } from '@/components/ui/IconButton';
import { EmptyState } from '@/components/ui/States';
import { useToast } from '@/components/ui/toast-context';
import {
  useAssignments,
  useAvailability,
  useCrews,
  useScheduleInvalidation,
} from '@/hooks/queries';

interface Props {
  open: boolean;
  onClose: () => void;
  /** The assignments to fill — whatever the board is showing. */
  assignments: Assignment[];
  /** The board's own window, widened below so rest is judged across its edges. */
  window: { from: number; to: number };
  personnel: Personnel[];
  qualifications: Qualification[];
  rules: SchedulingRule[];
  timezone: string;
  /**
   * A gap the algorithm cannot resolve on its own — a rule it will not
   * silently break — still ends in a person's hands. This jumps straight to
   * that assignment's own candidate list, where the blocked seat and the
   * override are one click away, instead of leaving the reader to find it on
   * the board themselves.
   */
  onOpenAssignment: (assignmentId: string) => void;
}

/**
 * How far past the board's window the proposal has to see.
 *
 * Sixteen hours of rest reaches into the previous evening and the next
 * morning, and a proposal that only knows about today would hand somebody the
 * 00:00 patrol straight off last night's 16:00 one. Two days each way covers
 * every rule the engine measures across a boundary.
 */
const CONTEXT_DAYS = 2;

const toEngineAssignment = (assignment: Assignment): EngineAssignment => ({
  id: assignment.id,
  assignmentTypeId: assignment.assignmentTypeId,
  title: assignment.title ?? assignment.assignmentTypeName,
  excludedQualificationIds: assignment.excludedQualificationIds,
  maxContinuousMinutes: assignment.maxContinuousMinutes,
  startAt: assignment.startAt,
  endAt: assignment.endAt,
  requiredHeadcount: assignment.requiredHeadcount,
  requiredQualifications: assignment.requiredQualifications,
  assigneeIds: assignment.assignees.map((assignee) => assignee.personnelId),
  assigneeRoles: Object.fromEntries(
    assignment.assignees.map((assignee) => [assignee.personnelId, assignee.role]),
  ),
  publicationState: assignment.publicationState,
  cancelled: assignment.status === 'cancelled',
});

const toEnginePerson = (person: Personnel): EnginePerson => ({
  id: person.id,
  displayName: person.displayName,
  qualificationIds: person.qualificationIds,
});

const toAbsences = (availability: Availability[]): EngineAbsence[] =>
  availability
    .filter((entry) => entry.status === 'approved' && entry.kind !== 'available')
    .map((entry) => ({
      personnelId: entry.personnelId,
      kind: entry.kind,
      startAt: entry.startAt,
      endAt: entry.endAt,
    }));

/**
 * The proposal is computed in the browser from data already on screen, using
 * the same engine the server enforces. That keeps a whole week's worth of
 * ranking off the request CPU budget, and the server re-validates everything
 * before writing, so the client is a convenience and never the authority.
 */
export function AutofillDialog({
  open,
  onClose,
  assignments,
  window,
  personnel,
  qualifications,
  rules,
  timezone,
  onOpenAssignment,
}: Props) {
  const toast = useToast();
  const invalidate = useScheduleInvalidation();
  const [dropped, setDropped] = useState<Set<string>>(new Set());

  // Fetched only while the dialog is open: the board polls its own window every
  // 45 seconds, and a second standing poll behind a closed dialog is waste.
  const context = { from: window.from - CONTEXT_DAYS * DAY, to: window.to + CONTEXT_DAYS * DAY };
  const around = useAssignments(context, open);
  const availability = useAvailability({ ...context, status: 'approved' }, open);
  const crews = useCrews();

  const proposal = useMemo(() => {
    if (!open) return null;
    const targets = assignments.map((assignment) => assignment.id);
    const known = around.data?.assignments ?? assignments;
    // The board's own rows always take part, even if the wider fetch has not
    // arrived yet: a proposal over a stale list would place the same person twice.
    const merged = [...known.filter((item) => !targets.includes(item.id)), ...assignments];
    return buildAutofillProposal({
      assignments: merged.map(toEngineAssignment),
      personnel: personnel.filter((person) => person.status === 'active').map(toEnginePerson),
      absences: toAbsences(availability.data ?? []),
      rules,
      assignmentIds: targets,
      qualificationNames: Object.fromEntries(
        qualifications.map((qualification) => [qualification.id, qualification.name]),
      ),
      exclusiveQualificationIds: qualifications
        .filter((qualification) => qualification.exclusive)
        .map((qualification) => qualification.id),
      // A post stood by fixed crews fills with one whole crew: the first seat
      // taken makes every other crew ineligible for the rest of the shift.
      crewsByType: crews.data ?? {},
      blockingQualificationIds: qualifications
        .filter((qualification) => qualification.blocksScheduling)
        .map((qualification) => qualification.id),
      timezone,
    });
  }, [
    open,
    assignments,
    around.data,
    personnel,
    availability.data,
    crews.data,
    qualifications,
    rules,
    timezone,
  ]);

  // The limit the demand is measured against, in hours.
  const continuousLimitHours =
    (rules.find((rule) => rule.code === 'MAX_CONTINUOUS')?.config.minutes ?? 0) / 60 || Infinity;

  const key = (item: ProposedAssignment) => `${item.assignmentId}:${item.personnelId}`;
  const accepted = (proposal?.proposed ?? []).filter((item) => !dropped.has(key(item)));

  const apply = useMutation({
    mutationFn: () =>
      api.post<{ applied: number; rejected: { reason: string }[] }>('/assignments/bulk-assign', {
        assignments: accepted.map((item) => ({
          assignmentId: item.assignmentId,
          personnelId: item.personnelId,
          role: item.role,
        })),
      }),
    onSuccess: (result) => {
      invalidate();
      toast.push('success', t('schedule.autofillApplied', { count: result.applied }));
      if (result.rejected.length > 0) {
        toast.push('error', t('schedule.autofillRejected', { count: result.rejected.length }));
      }
      setDropped(new Set());
      onClose();
    },
    onError: (error) =>
      toast.push('error', error instanceof ApiError ? error.message : t('state.errorBody')),
  });

  const byAssignment = new Map<string, ProposedAssignment[]>();
  for (const item of accepted) {
    byAssignment.set(item.assignmentId, [...(byAssignment.get(item.assignmentId) ?? []), item]);
  }

  return (
    <Dialog
      open={open}
      size="lg"
      title={t('schedule.autofillTitle')}
      description={t('schedule.autofillSubtitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('app.cancel')}
          </Button>
          <Button
            icon={<Sparkles className="size-4" />}
            disabled={accepted.length === 0}
            loading={apply.isPending}
            onClick={() => apply.mutate()}
          >
            {t('schedule.autofillApply', { count: accepted.length })}
          </Button>
        </>
      }
    >
      {!proposal || (proposal.proposed.length === 0 && proposal.gaps.length === 0) ? (
        <EmptyState description={t('schedule.autofillEmpty')} />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-2">
            <Badge tone="brand">{t('schedule.autofillProposed', { count: accepted.length })}</Badge>
            {proposal.gaps.length > 0 ? (
              <Badge tone="warning">
                {t('schedule.autofillGaps', {
                  count: proposal.gaps.reduce((total, gap) => total + gap.missing, 0),
                })}
              </Badge>
            ) : null}
            {proposal.swaps > 0 ? (
              <Badge tone="info">{t('schedule.autofillSwaps', { count: proposal.swaps })}</Badge>
            ) : null}
          </div>

          <ul className="flex flex-col gap-3">
            {[...byAssignment.entries()].map(([assignmentId, items]) => {
              const assignment = assignments.find((item) => item.id === assignmentId);
              return (
                <li
                  key={assignmentId}
                  className="rounded-[var(--radius-control)] border border-border-subtle p-3"
                >
                  <p className="font-medium">
                    {assignment?.title ?? assignment?.assignmentTypeName ?? assignmentId}
                  </p>
                  {assignment ? (
                    <p className="ltr-inline text-xs text-ink-muted">
                      {formatRange(assignment.startAt, assignment.endAt, timezone)}
                    </p>
                  ) : null}
                  <ul className="mt-2 flex flex-col gap-1.5">
                    {items.map((item) => (
                      <li key={key(item)} className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="w-16 text-xs font-semibold text-ink-muted">
                          {item.roleLabel}
                        </span>
                        <span className="font-medium">{item.displayName}</span>
                        <span className="text-xs text-ink-muted">{item.reasons.join(' · ')}</span>
                        {item.warnings.map((warning) => (
                          <span key={warning} className="text-xs text-warning">
                            {warning}
                          </span>
                        ))}
                        <IconButton
                          className="ms-auto"
                          label={t('schedule.autofillRemove')}
                          icon={<X className="size-4" />}
                          onClick={() => setDropped(new Set(dropped).add(key(item)))}
                        />
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}
          </ul>

          {proposal.gaps.length > 0 ? (
            <section>
              <h3 className="mb-1.5 text-sm font-semibold text-warning">
                {t('schedule.autofillGaps', {
                  count: proposal.gaps.reduce((total, gap) => total + gap.missing, 0),
                })}
              </h3>

              {/* When the seats outnumber the roster, the gap list is a symptom
                  and the arithmetic is the answer. */}
              <p className="mb-2 rounded-[var(--radius-control)] bg-surface-sunken p-2 text-xs text-ink-muted">
                {t('schedule.demand', {
                  hours: formatHours(proposal.demand.seatHours),
                  people: proposal.demand.people,
                  perPerson: formatHours(proposal.demand.hoursPerPerson),
                })}
                {proposal.demand.hoursPerPerson > continuousLimitHours ? (
                  <span className="mt-1 block font-medium text-warning">
                    {t('schedule.demandOverLimit')}
                  </span>
                ) : null}
              </p>
              <ul className="flex flex-col gap-1 text-sm text-ink-muted">
                {proposal.gaps.map((gap) => (
                  <li key={gap.assignmentId}>
                    <button
                      type="button"
                      className="font-medium text-ink underline-offset-2 hover:underline"
                      onClick={() => {
                        onClose();
                        onOpenAssignment(gap.assignmentId);
                      }}
                    >
                      {gap.assignmentTitle}
                    </button>{' '}
                    — {t('schedule.missingPerson', { count: gap.missing })}: {gap.reason}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}
